import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, message, Spin, Tag, Input, DatePicker } from 'antd';
import axiosInstance from '../../../axiosInstance';
import moment from 'moment';

const { RangePicker } = DatePicker;

const PurchaseOrdersList = () => {
    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [dateRange, setDateRange] = useState([null, null]);

    useEffect(() => {
        fetchPurchaseOrders();
    }, []);

    const fetchPurchaseOrders = async (query = '', startDate = null, endDate = null) => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/purchase-orders', {
                params: {
                    search: query,
                    start_date: startDate,
                    end_date: endDate,
                },
            });
            setPurchaseOrders(response.data);
        } catch (error) {
            message.error('Failed to fetch purchase orders');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        fetchPurchaseOrders(query, dateRange[0], dateRange[1]);
    };

    const handleDateChange = (dates) => {
        const [start, end] = dates || [];
        const formattedStartDate = start ? moment(start).format('YYYY-MM-DD') : null;
        const formattedEndDate = end ? moment(end).format('YYYY-MM-DD') : null;
        setDateRange([formattedStartDate, formattedEndDate]);
        fetchPurchaseOrders(searchQuery, formattedStartDate, formattedEndDate);
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this order?',
            onOk: async () => {
                try {
                    setLoading(true);
                    await axiosInstance.delete(`/purchase-orders/${id}`);
                    setPurchaseOrders(purchaseOrders.filter(order => order.id !== id));
                    message.success('Purchase order deleted successfully');
                } catch (error) {
                    message.error('Failed to delete purchase order');
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleEdit = (id) => {
        window.location.href = `/purchase-orders/edit/${id}`;
    };

    const handleView = (id) => {
        window.location.href = `/purchase-orders/view/${id}`;
    };

    const getStatusTag = (status) => {
        switch (status) {
            case 'drafted':
                return <Tag color="default" style={{ borderRadius: '12px', fontSize: '14px' }}>Drafted</Tag>;
            case 'processed':
                return <Tag color="orange" style={{ borderRadius: '12px', fontSize: '14px' }}>Processed</Tag>;
            case 'approved':
                return <Tag color="green" style={{ borderRadius: '12px', fontSize: '14px' }}>Approved</Tag>;
            case 'received':
                return <Tag color="blue" style={{ borderRadius: '12px', fontSize: '14px' }}>Received</Tag>;
            case 'canceled':
                return <Tag color="red" style={{ borderRadius: '12px', fontSize: '14px' }}>Canceled</Tag>;
            default:
                return <Tag color="default" style={{ borderRadius: '12px', fontSize: '14px' }}>Unknown</Tag>;
        }
    };

    const columns = [
        {
            title: 'Order Number',
            dataIndex: 'order_number',
            key: 'order_number',
        },
        {
            title: 'Supplier',
            dataIndex: ['supplier', 'name'],
            key: 'supplier_id',
            render: (name) => name || 'N/A',
        },
        {
            title: 'Order Date',
            dataIndex: 'order_date',
            key: 'order_date',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => getStatusTag(status),
        },
        {
            title: 'Actions',
            render: (text, record) => (
                <div style={{ display: 'flex', gap: '10px' }}>
                    <Button onClick={() => handleView(record.id)}>View</Button>
                    <Button
                        onClick={() => handleEdit(record.id)}
                        disabled={['approved', 'received', 'canceled'].includes(record.status)}
                    >
                        Edit
                    </Button>
                    <Button
                        onClick={() => handleDelete(record.id)}
                        disabled={['approved', 'received', 'canceled'].includes(record.status)}
                    >
                        Delete
                    </Button>
                </div>
            ),
        },
    ];

    return (
        <Spin spinning={loading} tip="Loading...">
            <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, gap: '16px' }}>
                    <Input
                        placeholder="Search by order number or supplier name"
                        value={searchQuery}
                        onChange={handleSearch}
                        style={{ width: '40%' }}
                    />
                    <RangePicker
                        onChange={handleDateChange}
                        format="YYYY-MM-DD"
                        style={{ width: '40%' }}
                    />
                </div>
                <Table
                    dataSource={purchaseOrders}
                    columns={columns}
                    rowKey="id"
                    pagination={{ pageSize: 50 }}
                    scroll={{ x: 'max-content' }}
                />
            </div>
        </Spin>
    );
};

export default PurchaseOrdersList;
